import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box } from 'portal-commons';
import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { CampaignDetail, campaignController } from '@/apis/core';
import { remapTCRStatus } from '../../utils';
import { convertTimeWithTimezone } from '@/utils/time';
import InfoItem from './InfoItem';
import InfoItemLayout from './InfoItemLayout';
import RcsSectionBlock from './RcsSectionBlock';
import StatusIndicator from './StatusIndicator';
import { humanize } from '@/utils/string';
import { ROUTES } from '@/constants/paths';

interface TenDlcCampaignFallbackSectionProps {
  rcsCampaignUid: string;
  fallbackCampaignUid: CampaignDetail['fallbackCampaignUid'];
  usecaseMap: Record<string, string>;
}

const TenDlcCampaignFallbackSection: FC<TenDlcCampaignFallbackSectionProps> = ({
  fallbackCampaignUid,
  usecaseMap = {},
}) => {
  const [fallbackCampaign, setFallbackCampaign] = useState<CampaignDetail>();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (fallbackCampaignUid) {
      campaignController.getCampaign(fallbackCampaignUid).then((data) => {
        if (data) {
          setFallbackCampaign(data);
        }
      });
    }

    return () => setFallbackCampaign(undefined);
  }, [fallbackCampaignUid]);

  return (
    <RcsSectionBlock
      icon={faBullhorn}
      title="10DLC Campaign Fallback Details"
      showDivider={true}
      data-testid="campaign10dlcFallbackDetails"
    >
      {fallbackCampaignUid ? (
        <InfoItemLayout.Vertical>
          <InfoItemLayout.Horizontal>
            <InfoItem title="10DLC Campaign ID">
              <Box
                role="link"
                sx={{ color: 't.blue100', cursor: 'pointer' }}
                onClick={() => {
                  history.push(
                    `${ROUTES.TEN_DLC_CAMPAIGNS}/${fallbackCampaignUid}`,
                    {
                      goBackPage: `${location.pathname}${location.search}`,
                    }
                  );
                }}
              >
                {fallbackCampaignUid}
              </Box>
            </InfoItem>
            <InfoItem title="Use Case">
              {fallbackCampaign &&
                (usecaseMap?.[fallbackCampaign.usecase] ??
                  fallbackCampaign.usecase)}
            </InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Registered On">
              {convertTimeWithTimezone(
                fallbackCampaign?.createDate,
                undefined,
                'MM/DD/YYYY'
              )}
            </InfoItem>
            <InfoItem title="TCR Status">
              {fallbackCampaign?.status && (
                <>
                  <StatusIndicator
                    variant={
                      fallbackCampaign?.status?.toLocaleLowerCase() === 'active'
                        ? 'success'
                        : 'error'
                    }
                  />
                  {humanize(remapTCRStatus(fallbackCampaign?.status))}
                </>
              )}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      ) : (
        <Box sx={{ my: 'm', textAlign: 'center', fontSize: 'H400' }}>
          No fallback campaign found
        </Box>
      )}
    </RcsSectionBlock>
  );
};

export default TenDlcCampaignFallbackSection;
